import {useCallback, useEffect, useMemo, useState} from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import {EmblaOptionsType} from "embla-carousel";

export const useMainCarousel = (count: number) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [dots, setDots] = useState<number[]>([]);

    const autoplay = useMemo(() => {
        if (count <= 1) return;

        return Autoplay({
            delay: 5000,
            stopOnInteraction: false,
            stopOnMouseEnter: true,
            stopOnFocusIn: true,
        });
    }, [count]);

    const options: EmblaOptionsType = {
        loop: count > 1,
    };

    const [carouselRef, carouselApi] = useEmblaCarousel(
        options,
        autoplay ? [autoplay] : [],
    );

    const onDotClick = useCallback(
        (index: number) => {
            carouselApi?.scrollTo(index);
        },
        [carouselApi],
    );

    const sync = useCallback(() => {
        if (!carouselApi) return;

        setSelectedIndex(carouselApi.selectedScrollSnap());
        setDots(carouselApi.scrollSnapList());
    }, [carouselApi]);

    const scrollPrev = useCallback(() => {
        autoplay?.stop();
        carouselApi?.scrollPrev();
        autoplay?.play();
    }, [autoplay, carouselApi]);

    const scrollNext = useCallback(() => {
        autoplay?.stop();
        carouselApi?.scrollNext();
        autoplay?.play();
    }, [autoplay, carouselApi]);

    useEffect(() => {
        if (!carouselApi) return;

        sync();

        carouselApi.on("select", sync);
        carouselApi.on("reInit", sync);

        return () => {
            carouselApi.off("select", sync);
            carouselApi.off("reInit", sync);
        };
    }, [carouselApi, sync]);

    return {
        carouselRef,
        carouselApi,
        selectedIndex,
        dots,
        onDotClick,
        scrollPrev,
        scrollNext,
    };
};
